import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { AuthSession, MonitoringRule } from "../api";
import { useI18n } from "../i18n";
import {
  ManagementDialog,
  ManagementDrawer,
  ManagementPanel,
  ManagementPagination,
} from "../managementUI";
import {
  date,
  duration,
  fresh,
  stateLabel,
  type MonitoringData,
} from "./model";
import { Badge, Empty, Note, Table } from "./ui";
import { SilenceDialog, type SilenceAction } from "./SilenceDialog";
import { NotificationChannels } from "./NotificationChannels";

export function AlertCenter({
  data,
  now,
  session,
  refresh,
}: {
  data: MonitoringData;
  now: number;
  session: AuthSession;
  refresh: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get("alerts_tab") || "active";
  const tab = ["active", "rules", "notifications", "silences"].includes(
    requestedTab,
  )
    ? requestedTab
    : "active";
  const searchKey = tab === "rules" ? "rule_search" : "alert_search";
  const search = params.get(searchKey) || "";
  const severity = params.get("alert_severity") || "all";
  const setSearch = (value: string) => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(searchKey, value);
        else next.delete(searchKey);
        return next;
      },
      { replace: true },
    );
  };
  const setSeverity = (value: string) => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("alert_severity", value);
        return next;
      },
      { replace: true },
    );
  };
  const showAudit = params.get("drawer") === "audit";
  const eventStatus = params.get("event_status") || "active";
  const closeAudit = () => {
    const next = new URLSearchParams(params);
    next.delete("drawer");
    setParams(next);
  };
  const [offset, setOffset] = useState(0);
  const [action, setAction] = useState<SilenceAction | null>(null);
  const [rule, setRule] = useState<MonitoringRule | null>(null);
  const snapshot = data.alerts.data;
  const current = fresh(data.alerts, now);
  const alertsReady = current && snapshot?.alerts_available;
  const rulesReady = current && snapshot?.rules_available;
  const silencesReady = current && snapshot?.silences_available;
  const alerts = (snapshot?.items ?? []).filter(
    (a) =>
      (severity === "all" || a.labels.severity === severity) &&
      [a.labels.alertname, a.labels.instance, a.annotations.summary]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const selected = (snapshot?.items ?? []).find(
    (a) => a.fingerprint === params.get("alert"),
  );
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
    setOffset(0);
  };
  const close = () => {
    const next = new URLSearchParams(params);
    next.delete("alert");
    setParams(next, { replace: true });
  };
  const tabs = [
    ["active", t("告警事件", "Alert events")],
    ["rules", t("告警规则", "Alert rules")],
    ["notifications", t("通知渠道", "Notification channels")],
    ["silences", t("静默", "Silences")],
  ];
  const rules = (snapshot?.rules ?? []).filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <>
      <div className="monitor-navigation">
        <nav
          className="monitor-subnav"
          aria-label={t("告警分区", "Alert sections")}
        >
          {tabs.map(([id, label]) => (
            <button
              key={id}
              className={id === tab ? "selected" : ""}
              aria-current={tab === id ? "page" : undefined}
              onClick={() => {
                update("alerts_tab", id);
              }}
            >
              {label}
            </button>
          ))}
        </nav>
        <button
          className="secondary-button"
          onClick={() => update("drawer", "audit")}
        >
          {t("操作记录", "Operation records")}
        </button>
      </div>
      {!current ? (
        <Note>
          {t(
            "告警数据不可用或已过期；若有缓存，仅用于查看，暂不允许变更。",
            "Alert data is unavailable or stale. Cached records are view-only until fresh data is available.",
          )}
        </Note>
      ) : null}
      {tab === "active" ? (
        <ManagementPanel title={t("告警事件", "Alert events")}>
          <div className="monitor-toolbar">
            <label>
              {t("事件状态", "Event state")}
              <select
                value={eventStatus}
                onChange={(e) => update("event_status", e.target.value)}
              >
                <option value="active">{t("当前告警", "Active alerts")}</option>
                <option value="resolved" disabled>
                  {t(
                    "已恢复（历史尚未接入）",
                    "Resolved (history not connected)",
                  )}
                </option>
              </select>
            </label>
            <label>
              {t("搜索告警", "Search alerts")}
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setOffset(0);
                }}
              />
            </label>
            <label>
              {t("级别", "Severity")}
              <select
                value={severity}
                onChange={(e) => {
                  setSeverity(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="all">{t("全部", "All")}</option>
                <option value="critical">Critical</option>
                <option value="warning">Warning</option>
              </select>
            </label>
          </div>
          {eventStatus === "resolved" ? (
            <Empty>
              {t(
                "历史留存尚未接入，无法查询已恢复告警。请选择当前告警。",
                "History is not connected. Resolved alerts cannot be queried; select active alerts.",
              )}
            </Empty>
          ) : !alertsReady && !alerts.length ? (
            <Empty>
              {t(
                "告警来源不可用，不能判断是否有告警。",
                "The alert source is unavailable; alert absence cannot be determined.",
              )}
            </Empty>
          ) : !alerts.length ? (
            <Empty>
              {t("没有匹配的活动告警。", "No matching active alerts.")}
            </Empty>
          ) : (
            <>
              <Table
                headers={[
                  t("告警", "Alert"),
                  t("级别", "Severity"),
                  t("状态", "State"),
                  t("持续", "Duration"),
                  t("影响对象", "Affected object"),
                  t("操作", "Actions"),
                ]}
              >
                {alerts.slice(offset, offset + 20).map((a) => (
                  <tr key={a.fingerprint}>
                    <td>
                      <strong>{a.labels.alertname}</strong>
                      <span>{a.annotations.summary}</span>
                    </td>
                    <td>{a.labels.severity || "—"}</td>
                    <td>{stateLabel(a.state, t)}</td>
                    <td>{duration((now - Date.parse(a.starts_at)) / 1000)}</td>
                    <td>{a.labels.instance || a.labels.job || "—"}</td>
                    <td>
                      <button
                        className="secondary-button"
                        onClick={() => update("alert", a.fingerprint)}
                      >
                        {t("详情", "Details")}
                      </button>
                    </td>
                  </tr>
                ))}
              </Table>
              <ManagementPagination
                total={alerts.length}
                offset={offset}
                limit={20}
                onChange={setOffset}
              />
            </>
          )}
        </ManagementPanel>
      ) : null}
      {tab === "rules" ? (
        <ManagementPanel
          title={t("有效规则配置", "Effective rule configuration")}
        >
          <Note>
            {t(
              "当前规则随部署发布，参数只读。规则计算健康与当前 DC 的触发情况分别展示。",
              "Rules are release-managed and read-only. Evaluation health is separate from firing alerts in this DC.",
            )}
          </Note>
          <div className="monitor-toolbar">
            <label>
              {t("搜索规则", "Search rules")}
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          </div>
          {!rulesReady ? (
            <Empty>{t("规则来源不可用。", "Rule source unavailable.")}</Empty>
          ) : !rules.length ? (
            <Empty>{t("没有匹配规则。", "No matching rules.")}</Empty>
          ) : (
            <Table
              headers={[
                t("规则", "Rule"),
                t("级别", "Severity"),
                t("持续条件", "For"),
                t("计算健康", "Evaluation"),
                t("操作", "Actions"),
              ]}
            >
              {rules.map((r) => (
                <tr key={r.name}>
                  <td>
                    <strong>{r.name}</strong>
                    <span>{r.annotations.summary}</span>
                  </td>
                  <td>{r.labels.severity || "—"}</td>
                  <td>{r.duration}</td>
                  <td>
                    <Badge state={r.health === "ok" ? "healthy" : "degraded"} />
                  </td>
                  <td>
                    <button
                      className="secondary-button"
                      onClick={() => setRule(r)}
                    >
                      {t("查看配置", "View configuration")}
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </ManagementPanel>
      ) : null}
      {tab === "silences" ? (
        <ManagementPanel
          title={t("临时静默", "Temporary silences")}
          actions={
            <button
              className="secondary-button"
              disabled={!silencesReady || !rulesReady}
              onClick={() => setAction({ kind: "create" })}
            >
              {t("创建静默", "Create silence")}
            </button>
          }
        >
          {!silencesReady ? (
            <Empty>
              {t("静默来源不可用。", "Silence source unavailable.")}
            </Empty>
          ) : !snapshot?.silences.length ? (
            <Empty>
              {t(
                "当前没有生效或待生效的静默。",
                "No active or pending silences.",
              )}
            </Empty>
          ) : (
            <Table
              headers={[
                t("规则 / 范围", "Rule / scope"),
                t("状态", "State"),
                t("结束时间", "Ends"),
                t("原因 / 操作者", "Reason / actor"),
                t("操作", "Actions"),
              ]}
            >
              {snapshot.silences.map((s) => (
                <tr key={s.silence_id}>
                  <td>
                    <strong>
                      {s.matchers.find((m) => m.name === "alertname")?.value ||
                        t("当前DC全部告警", "All alerts in this DC")}
                    </strong>
                    <span>{s.silence_id}</span>
                  </td>
                  <td>
                    {s.state === "active"
                      ? t("生效中", "Active")
                      : stateLabel(s.state, t)}
                  </td>
                  <td>{date(s.ends_at)}</td>
                  <td>
                    <strong>{s.comment}</strong>
                    <span>{s.created_by}</span>
                  </td>
                  <td>
                    <button
                      className="secondary-button"
                      onClick={() => setAction({ kind: "expire", silence: s })}
                    >
                      {t("结束静默", "End silence")}
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
          )}
          <Note>
            {t(
              "周期维护窗口尚未接入。临时静默只影响指定规则的通知。",
              "Recurring maintenance windows are not connected. A temporary silence affects notifications for its selected rule.",
            )}
          </Note>
        </ManagementPanel>
      ) : null}
      {tab === "notifications" ? (
        <NotificationChannels session={session} />
      ) : null}
      {showAudit ? (
        <ManagementDrawer
          onClose={closeAudit}
          title={t("最近告警处置操作", "Recent alert operations")}
        >
          <Note>
            {t(
              "取最近100条管理操作中的监控操作，非完整告警历史。",
              "Monitoring actions among the latest 100 management operations; not complete alert history.",
            )}
          </Note>
          {!fresh(data.changes, now) || !data.changes.data?.available ? (
            <Empty>{t("审计来源不可用。", "Audit source unavailable.")}</Empty>
          ) : (
            <Table
              headers={[
                t("操作", "Operation"),
                t("目标", "Target"),
                t("原因", "Reason"),
                t("结果", "Result"),
                t("时间", "Time"),
              ]}
            >
              {data.changes.data.items
                .filter((i) => i.kind.startsWith("alert."))
                .map((i) => (
                  <tr key={i.id}>
                    <td>{i.kind}</td>
                    <td>{i.resource_id}</td>
                    <td>
                      <strong>{i.reason}</strong>
                      <span>{i.actor}</span>
                    </td>
                    <td>{stateLabel(i.state, t)}</td>
                    <td>{date(i.updated_at)}</td>
                  </tr>
                ))}
            </Table>
          )}
        </ManagementDrawer>
      ) : null}
      {selected && !showAudit ? (
        <ManagementDialog
          title={selected.labels.alertname || t("告警详情", "Alert details")}
          onClose={close}
        >
          <p>{selected.annotations.summary}</p>
          <p>{selected.annotations.description}</p>
          <dl className="monitor-detail">
            <dt>{t("影响对象", "Affected object")}</dt>
            <dd>{selected.labels.instance || selected.labels.job || "—"}</dd>
            <dt>{t("状态", "State")}</dt>
            <dd>{stateLabel(selected.state, t)}</dd>
            <dt>{t("开始时间", "Started")}</dt>
            <dd>{date(selected.starts_at)}</dd>
            <dt>{t("最近更新", "Updated")}</dt>
            <dd>{date(selected.updated_at)}</dd>
          </dl>
          <div className="dialog-actions">
            <button
              className="secondary-button"
              disabled={!silencesReady}
              onClick={() => {
                close();
                setAction({
                  kind: "create",
                  alertName: selected.labels.alertname,
                });
              }}
            >
              {t("静默此规则", "Silence this rule")}
            </button>
            <button className="secondary-button" onClick={close}>
              {t("关闭", "Close")}
            </button>
          </div>
        </ManagementDialog>
      ) : null}
      {rule ? (
        <ManagementDialog title={rule.name} onClose={() => setRule(null)}>
          <p>{rule.annotations.summary}</p>
          <pre className="monitor-expression">{rule.query}</pre>
          <dl className="monitor-detail">
            <dt>{t("持续时间", "For")}</dt>
            <dd>{rule.duration}</dd>
            <dt>{t("严重程度", "Severity")}</dt>
            <dd>{rule.labels.severity || "—"}</dd>
            <dt>{t("计算错误", "Evaluation error")}</dt>
            <dd>{rule.last_error || "—"}</dd>
          </dl>
          <button className="secondary-button" onClick={() => setRule(null)}>
            {t("关闭", "Close")}
          </button>
        </ManagementDialog>
      ) : null}
      {action ? (
        <SilenceDialog
          action={action}
          rules={snapshot?.rules ?? []}
          session={session}
          onClose={() => setAction(null)}
          onSuccess={refresh}
        />
      ) : null}
    </>
  );
}
